import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { EMPTY_FORM } from "../components/wizard/constants";
import {
    validateBrief,
    validateContact,
} from "../components/wizard/validation";

// API base. When REACT_APP_BACKEND_URL is empty (e.g. single-app Hostinger deploy
// where the Node server also serves this build), calls fall back to a relative /api.
const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const TOAST_STYLE = {
    background: "#000000",
    border: "1px solid #14b8a6",
    color: "#ffffff",
    boxShadow: "0 0 24px rgba(20,184,166,0.45)",
};

/**
 * Two-stage lead capture.
 * Step 1 (contact details) is submitted & emailed on "Continue"; step 2 (the
 * optimisation brief) is submitted & emailed on final submit and linked to the
 * same lead via its id.
 */
export const useLeadForm = () => {
    const [step, setStep] = useState(1);
    const [leadId, setLeadId] = useState(null);
    const [submittingContact, setSubmittingContact] = useState(false);
    const [submittingBrief, setSubmittingBrief] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);

    const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

    // Step 1 → email contact details, then advance.
    const submitContact = async (event) => {
        event.preventDefault();
        if (!validateContact(form)) return; // validates the contact fields

        setSubmittingContact(true);
        try {
            const { data } = await axios.post(
                `${API}/leads/contact`,
                {
                    company_size: form.company_size,
                    name: form.name,
                    email: form.email,
                    company_name: form.company_name,
                    country_code: form.country_code,
                    contact_number: form.contact_number,
                },
                { timeout: 20000 },
            );
            setLeadId(data.id);
            toast.success("Details received.", {
                description: "One more quick step to go.",
                icon: <Check size={18} />,
                style: TOAST_STYLE,
            });
            setStep(2);
        } catch (err) {
            toast.error("Couldn't send your details. Please try again.");
        } finally {
            setSubmittingContact(false);
        }
    };

    const goBack = () => setStep(1);

    // Step 2 → email the brief, linked to the same lead.
    const submitBrief = async (event) => {
        event.preventDefault();
        if (!validateBrief(form)) return; // validates the brief fields

        setSubmittingBrief(true);
        try {
            await axios.post(
                `${API}/leads/brief`,
                {
                    id: leadId,
                    pain_point: form.pain_point,
                    bottleneck: form.bottleneck,
                },
                { timeout: 20000 },
            );
            toast.success("Briefing transmitted successfully.", {
                description: `We'll be in touch with ${form.name.split(" ")[0]} shortly.`,
                icon: <Check size={18} />,
                style: TOAST_STYLE,
            });
            setForm(EMPTY_FORM);
            setLeadId(null);
            setStep(1);
        } catch (err) {
            toast.error("Transmission failed. Please try again.");
        } finally {
            setSubmittingBrief(false);
        }
    };

    return {
        step,
        form,
        submittingContact,
        submittingBrief,
        set,
        submitContact,
        goBack,
        submitBrief,
    };
};
